import type { GridFilterModel, GridPinnedColumnFields, GridSortModel } from '@repo/go-table';
import { makeAutoObservable } from 'mobx';

import type { WidgetStateParams, WidgetView } from '../types';
import type { WidgetStatePublisher } from '../WidgetStatePublisher';

export type TTableSettingsModel = {
  columnsOrderModel?: string[];
  columnsVisibility?: Record<string, boolean>;
  columnsWidth?: Record<string, number>;
  sortingModel?: GridSortModel;
  filterModel?: GridFilterModel;
  pinnedColumns?: GridPinnedColumnFields;
  contextColumnsHidden?: boolean;
};

const getDefaultFilterModel = (): GridFilterModel => ({ items: [] });

export class SettingsStore {
  view: WidgetView;

  scaleDepthUnit: 'm' | 'ft';

  alertRulesTimeSettings: TTableSettingsModel;
  alertRulesDepthSettings: TTableSettingsModel;
  autoAnomaliesSettings: TTableSettingsModel;
  manualAnomaliesSettings: TTableSettingsModel;

  constructor(private readonly widgetStatePublisher: WidgetStatePublisher) {
    this.view = widgetStatePublisher.params.view ?? 'alertRulesTime';
    this.scaleDepthUnit = widgetStatePublisher.params.scaleDepthUnit ?? 'm';

    this.alertRulesTimeSettings = {
      columnsOrderModel: widgetStatePublisher.params.alertRulesTimeColumnsOrder ?? [],
      columnsVisibility: widgetStatePublisher.params.alertRulesTimeColumnsVisibility ?? {},
      columnsWidth: widgetStatePublisher.params.alertRulesTimeColumnsWidth ?? {},
      sortingModel: widgetStatePublisher.params.alertRulesTimeSortingModel ?? [],
      filterModel: widgetStatePublisher.params.alertRulesTimeFilterModel ?? getDefaultFilterModel(),
      pinnedColumns: widgetStatePublisher.params.alertRulesTimePinnedColumns ?? {},
    };

    this.alertRulesDepthSettings = {
      columnsOrderModel: widgetStatePublisher.params.alertRulesDepthColumnsOrder ?? [],
      columnsVisibility: widgetStatePublisher.params.alertRulesDepthColumnsVisibility ?? {},
      columnsWidth: widgetStatePublisher.params.alertRulesDepthColumnsWidth ?? {},
      sortingModel: widgetStatePublisher.params.alertRulesDepthSortingModel ?? [],
      filterModel: widgetStatePublisher.params.alertRulesDepthFilterModel ?? getDefaultFilterModel(),
      pinnedColumns: widgetStatePublisher.params.alertRulesDepthPinnedColumns ?? {},
    };

    this.autoAnomaliesSettings = {
      columnsOrderModel: widgetStatePublisher.params.autoAnomaliesColumnsOrder ?? [],
      columnsVisibility: widgetStatePublisher.params.autoAnomaliesColumnsVisibility ?? {},
      columnsWidth: widgetStatePublisher.params.autoAnomaliesColumnsWidth ?? {},
      sortingModel: widgetStatePublisher.params.autoAnomaliesSortingModel ?? [],
      filterModel: widgetStatePublisher.params.autoAnomaliesFilterModel ?? getDefaultFilterModel(),
      pinnedColumns: widgetStatePublisher.params.autoAnomaliesPinnedColumns ?? {},
      contextColumnsHidden: widgetStatePublisher.params.autoAnomaliesContextColumnsHidden ?? false,
    };

    this.manualAnomaliesSettings = {
      columnsOrderModel: widgetStatePublisher.params.manualAnomaliesColumnsOrder ?? [],
      columnsVisibility: widgetStatePublisher.params.manualAnomaliesColumnsVisibility ?? {},
      columnsWidth: widgetStatePublisher.params.manualAnomaliesColumnsWidth ?? {},
      sortingModel: widgetStatePublisher.params.manualAnomaliesSortingModel ?? [],
      filterModel: widgetStatePublisher.params.manualAnomaliesFilterModel ?? getDefaultFilterModel(),
      pinnedColumns: widgetStatePublisher.params.manualAnomaliesPinnedColumns ?? {},
      contextColumnsHidden: widgetStatePublisher.params.manualAnomaliesContextColumnsHidden ?? false,
    };

    makeAutoObservable(this);
  }

  setTableView(table: WidgetStateParams['view']) {
    this.view = table;
    this.widgetStatePublisher.setParams({ view: table });
  }

  setScaleDepthUnit(scaleDepthUnit: 'm' | 'ft') {
    this.scaleDepthUnit = scaleDepthUnit;
    this.widgetStatePublisher.setParams({ scaleDepthUnit: scaleDepthUnit });
  }

  getAlertRulesSettings(index: 'time' | 'depth'): TTableSettingsModel {
    return index === 'time' ? this.alertRulesTimeSettings : this.alertRulesDepthSettings;
  }

  setAlertRulesSettings(index: 'time' | 'depth', alertRulesSettings: TTableSettingsModel) {
    if (index === 'time') {
      this.alertRulesTimeSettings = alertRulesSettings;
      this.widgetStatePublisher.setParams({
        alertRulesTimeColumnsOrder: alertRulesSettings.columnsOrderModel ?? [],
        alertRulesTimeColumnsVisibility: alertRulesSettings.columnsVisibility ?? {},
        alertRulesTimeColumnsWidth: alertRulesSettings.columnsWidth ?? {},
        alertRulesTimeSortingModel: alertRulesSettings.sortingModel ?? [],
        alertRulesTimeFilterModel: alertRulesSettings.filterModel ?? getDefaultFilterModel(),
        alertRulesTimePinnedColumns: alertRulesSettings.pinnedColumns ?? {},
      });
      return;
    }

    this.alertRulesDepthSettings = alertRulesSettings;
    this.widgetStatePublisher.setParams({
      alertRulesDepthColumnsOrder: alertRulesSettings.columnsOrderModel ?? [],
      alertRulesDepthColumnsVisibility: alertRulesSettings.columnsVisibility ?? {},
      alertRulesDepthColumnsWidth: alertRulesSettings.columnsWidth ?? {},
      alertRulesDepthSortingModel: alertRulesSettings.sortingModel ?? [],
      alertRulesDepthFilterModel: alertRulesSettings.filterModel ?? getDefaultFilterModel(),
      alertRulesDepthPinnedColumns: alertRulesSettings.pinnedColumns ?? {},
    });
  }

  getAutoAnomaliesSettings(): TTableSettingsModel {
    return this.autoAnomaliesSettings;
  }

  setAutoAnomaliesSettings(settings: TTableSettingsModel) {
    this.autoAnomaliesSettings = settings;
    this.widgetStatePublisher.setParams({
      autoAnomaliesColumnsOrder: settings.columnsOrderModel ?? [],
      autoAnomaliesColumnsVisibility: settings.columnsVisibility ?? {},
      autoAnomaliesColumnsWidth: settings.columnsWidth ?? {},
      autoAnomaliesSortingModel: settings.sortingModel ?? [],
      autoAnomaliesFilterModel: settings.filterModel ?? getDefaultFilterModel(),
      autoAnomaliesPinnedColumns: settings.pinnedColumns ?? {},
      autoAnomaliesContextColumnsHidden: settings.contextColumnsHidden ?? false,
    });
  }

  getManualAnomaliesSettings(): TTableSettingsModel {
    return this.manualAnomaliesSettings;
  }

  setManualAnomaliesSettings(settings: TTableSettingsModel) {
    this.manualAnomaliesSettings = settings;
    this.widgetStatePublisher.setParams({
      manualAnomaliesColumnsOrder: settings.columnsOrderModel ?? [],
      manualAnomaliesColumnsVisibility: settings.columnsVisibility ?? {},
      manualAnomaliesColumnsWidth: settings.columnsWidth ?? {},
      manualAnomaliesSortingModel: settings.sortingModel ?? [],
      manualAnomaliesFilterModel: settings.filterModel ?? getDefaultFilterModel(),
      manualAnomaliesPinnedColumns: settings.pinnedColumns ?? {},
      manualAnomaliesContextColumnsHidden: settings.contextColumnsHidden ?? false,
    });
  }
}
